import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import PaymentRequestDetail from '../components/PaymentRequestDetail';
import { api } from '../utils/api';

interface PaymentRequest {
  id: number;
  // buyer_login удален, используем только customer_name
  customer_name?: string;
  operation_token?: string | null;
  shop_process_id?: string | null;
  amount: number;
  reserved_amount: number;
  currency: string;
  status: string;
  created_at: string;
  expires_at: string;
}

const PaymentRequests: React.FC = () => {
  const { user, logout } = useAuth();
  const [paymentRequests, setPaymentRequests] = useState<PaymentRequest[]>([]);
  const [filteredRequests, setFilteredRequests] = useState<PaymentRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedRequest, setSelectedRequest] = useState<PaymentRequest | null>(null);

  const getExternalId = (request: PaymentRequest) => request.shop_process_id || request.operation_token || null;
  const getDisplayId = (request: PaymentRequest) => getExternalId(request) || `#${request.id}`;

  useEffect(() => {
    if (user?.role === 'operator') {
      fetchPaymentRequests();
      
      // Автоматическое обновление каждые 5 секунд
      const interval = setInterval(() => {
        fetchPaymentRequests();
      }, 5000);
      
      return () => clearInterval(interval);
    }
  }, [user]);

  useEffect(() => {
    filterAndSortRequests();
  }, [paymentRequests, searchTerm, statusFilter, sortBy, sortOrder]);

  const fetchPaymentRequests = async () => {
    try {
      const response = await api.get('/operator/payments');
      const data = (response.data || []) as PaymentRequest[];
      setPaymentRequests(data);
    } catch (error) {
      console.error('Error fetching payment requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortRequests = () => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    let filtered = paymentRequests.filter(request => {
      const login = (request.customer_name || '').toLowerCase();
      const externalId = (request.shop_process_id || request.operation_token || '').toString().toLowerCase();
      const matchesSearch = login.includes(normalizedSearch) ||
                           request.id.toString().includes(searchTerm) ||
                           externalId.includes(normalizedSearch);
      const matchesStatus = statusFilter === 'all' || request.status === statusFilter;
      return matchesSearch && matchesStatus;
    });

    // Сортировка
    filtered.sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (sortBy) {
        case 'amount':
          aValue = a.amount;
          bValue = b.amount;
          break;
        case 'created_at':
          aValue = new Date(a.created_at).getTime();
          bValue = new Date(b.created_at).getTime();
          break;
        case 'customer_name':
          aValue = (a.customer_name || '').toLowerCase();
          bValue = (b.customer_name || '').toLowerCase();
          break;
        default:
          aValue = a.id;
          bValue = b.id;
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredRequests(filtered);
  };

  const formatAmount = (amount: number) => {
    return amount.toString();
  };

  const formatStatus = (status: string) => {
    const statusMap: { [key: string]: { text: string; color: string; bgColor: string } } = {
      'pending': { 
        text: 'Ожидает оплаты', 
        color: 'text-yellow-800', 
        bgColor: 'bg-yellow-100' 
      },
      'confirmed': { 
        text: 'Подтвержден', 
        color: 'text-green-800', 
        bgColor: 'bg-green-100' 
      },
      'expired': { 
        text: 'Истек', 
        color: 'text-red-800', 
        bgColor: 'bg-red-100' 
      },
      'rejected': { 
        text: 'Отклонен', 
        color: 'text-red-800', 
        bgColor: 'bg-red-100' 
      }
    };
    
    const statusInfo = statusMap[status] || { 
      text: status, 
      color: 'text-gray-800', 
      bgColor: 'bg-gray-100' 
    };
    
    return (
      <span className={`px-3 py-1 text-xs font-medium rounded-full ${statusInfo.bgColor} ${statusInfo.color}`}>
        {statusInfo.text}
      </span>
    );
  };

  const getCurrencySymbol = (currency: string) => {
    const symbols: { [key: string]: string } = {
      'RUB': '₽',
      'KZT': '₸',
      'USD': '$',
      'EUR': '€'
    };
    return symbols[currency] || currency;
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const getStatusCount = (status: string) => {
    return paymentRequests.filter(req => req.status === status).length;
  };

  const handleViewRequest = (request: PaymentRequest) => {
    setSelectedRequest(request);
  };

  const handleConfirmRequest = async (request: PaymentRequest) => {
    try {
      await api.post('/operator/payments/manual', { paymentId: request.id, action: 'confirm' });
      await fetchPaymentRequests();
      setSelectedRequest(null);
    } catch (error) {
      console.error('Error confirming payment:', error);
      const errMsg = (error as any)?.response?.data?.error || (error as any)?.message || 'Ошибка при подтверждении платежа';
      alert(errMsg);
    }
  };

  const handleRejectRequest = async (request: PaymentRequest) => {
    try {
      await api.post('/operator/payments/manual', { paymentId: request.id, action: 'reject' });
      await fetchPaymentRequests();
      setSelectedRequest(null);
    } catch (error) {
      console.error('Error rejecting payment:', error);
      const errMsg = (error as any)?.response?.data?.error || (error as any)?.message || 'Ошибка при отклонении платежа';
      alert(errMsg);
    }
  };

  if (user?.role !== 'operator') {
    return <div className="text-center text-red-600">Доступ запрещен</div>;
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Навигация */}
      <div className="bg-white border border-gray-200 rounded-lg mb-6">
        <div className="px-6 py-4">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-2xl font-bold text-gray-900">Платежные заявки</h1>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">
                Пользователь: <span className="font-medium">{user.username}</span>
              </span>
              <button
                onClick={logout}
                className="px-3 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
              >
                Выйти
              </button>
            </div>
          </div>
          
          <nav className="flex space-x-6">
            <Link
              to="/dashboard"
              className="text-gray-500 hover:text-gray-700 px-3 py-2 text-sm font-medium border-b-2 border-transparent hover:border-gray-300"
            >
              ← Дашборд
            </Link>
            <Link
              to="/payment-requisites"
              className="text-gray-500 hover:text-gray-700 px-3 py-2 text-sm font-medium border-b-2 border-transparent hover:border-gray-300"
            >
              Реквизиты
            </Link>
            <Link
              to="/buyers"
              className="text-gray-500 hover:text-gray-700 px-3 py-2 text-sm font-medium border-b-2 border-transparent hover:border-gray-300"
            >
              Покупатели
            </Link>
            <Link
              to="/audit-logs"
              className="text-gray-500 hover:text-gray-700 px-3 py-2 text-sm font-medium border-b-2 border-transparent hover:border-gray-300"
            >
              Аудит
            </Link>
          </nav>
        </div>
      </div>

      {/* Статистика */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                <span className="text-white text-sm font-medium">📊</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Всего заявок</p>
              <p className="text-2xl font-bold text-gray-900">{paymentRequests.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-yellow-500 rounded-md flex items-center justify-center">
                <span className="text-white text-sm font-medium">⏳</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Ожидают оплаты</p>
              <p className="text-2xl font-bold text-yellow-600">{getStatusCount('pending')}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                <span className="text-white text-sm font-medium">✅</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Подтверждено</p>
              <p className="text-2xl font-bold text-green-600">{getStatusCount('confirmed')}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-red-500 rounded-md flex items-center justify-center">
                <span className="text-white text-sm font-medium">❌</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Истекло/Отклонено</p>
              <p className="text-2xl font-bold text-red-600">{getStatusCount('expired') + getStatusCount('rejected')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Фильтры и поиск */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Фильтры и поиск</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Поиск</label>
              <input
                type="text"
                placeholder="ID заявки или логин покупателя..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Статус</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">Все статусы</option>
                <option value="pending">Ожидает оплаты</option>
                <option value="confirmed">Подтверждено</option>
                <option value="expired">Истекло</option>
                <option value="rejected">Отклонено</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Сортировка</label>
              <select
                value={`${sortBy}-${sortOrder}`}
                onChange={(e) => {
                  const [field, order] = e.target.value.split('-');
                  setSortBy(field);
                  setSortOrder(order as 'asc' | 'desc');
                }}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="created_at-desc">Дата создания (новые)</option>
                <option value="created_at-asc">Дата создания (старые)</option>
                <option value="amount-desc">Сумма (по убыванию)</option>
                <option value="amount-asc">Сумма (по возрастанию)</option>
                <option value="customer_name-asc">Покупатель (А-Я)</option>
                <option value="customer_name-desc">Покупатель (Я-А)</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Список платежных заявок */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">
              Список заявок ({filteredRequests.length})
            </h3>
            <div className="text-sm text-gray-500">
              Показано {filteredRequests.length} из {paymentRequests.length}
            </div>
          </div>
        </div>
        
        {filteredRequests.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <div className="text-gray-400 text-6xl mb-4">📭</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Заявки не найдены</h3>
            <p className="text-gray-500">
              {searchTerm || statusFilter !== 'all' 
                ? 'Попробуйте изменить фильтры или поисковый запрос'
                : 'Платежные заявки отсутствуют'
              }
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('id')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>ID</span>
                      {sortBy === 'id' && (
                        <span className="text-blue-600">
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('customer_name')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Покупатель</span>
                      {sortBy === 'customer_name' && (
                        <span className="text-blue-600">
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('amount')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Сумма</span>
                      {sortBy === 'amount' && (
                        <span className="text-blue-600">
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Статус
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('created_at')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Создана</span>
                      {sortBy === 'created_at' && (
                        <span className="text-blue-600">
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Истекает
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Действия
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredRequests.map((request) => {
                  const externalId = getExternalId(request);
                  const displayId = getDisplayId(request);
                  return (
                    <tr key={request.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {displayId.startsWith('#') ? displayId : `#${displayId}`}
                        </div>
                        {/* Внутренний ID не показываем в списке, чтобы везде был один “номер” */}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{request.customer_name || '—'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {formatAmount(request.amount)} {getCurrencySymbol(request.currency)}
                        </div>
                        {request.reserved_amount !== request.amount && (
                          <div className="text-xs text-blue-600">
                            Резерв: {formatAmount(request.reserved_amount)} {getCurrencySymbol(request.currency)}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {formatStatus(request.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {new Date(request.created_at).toLocaleDateString('ru-RU')}
                        </div>
                        <div className="text-xs text-gray-500">
                          {new Date(request.created_at).toLocaleTimeString('ru-RU')}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {new Date(request.expires_at).toLocaleDateString('ru-RU')}
                        </div>
                        <div className="text-xs text-gray-500">
                          {new Date(request.expires_at).toLocaleTimeString('ru-RU')}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button 
                            onClick={() => handleViewRequest(request)}
                            className="text-blue-600 hover:text-blue-900 bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded text-xs"
                          >
                            Просмотр
                          </button>
                          {request.status === 'pending' && (
                            <>
                              <button 
                                onClick={() => handleConfirmRequest(request)}
                                className="text-green-600 hover:text-green-900 bg-green-50 hover:bg-green-100 px-2 py-1 rounded text-xs"
                              >
                                Подтвердить
                              </button>
                              <button 
                                onClick={() => handleRejectRequest(request)}
                                className="text-red-600 hover:text-red-900 bg-red-50 hover:bg-red-100 px-2 py-1 rounded text-xs"
                              >
                                Отклонить
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Модальное окно детального просмотра */}
      {/* 
        Step 1: Fix type error by ensuring 'buyer_login' is always a string.
        Step 2: Cast 'selectedRequest' to the correct type if necessary, or provide a fallback for 'buyer_login'.
        Step 3: Defensive check to ensure 'selectedRequest' has all required fields.
      */}
      {selectedRequest && selectedRequest.customer_name && (
        <PaymentRequestDetail
          request={{
            ...selectedRequest,
            customer_name: selectedRequest.customer_name ?? ''
          }}
          onClose={() => setSelectedRequest(null)}
          onConfirm={() => handleConfirmRequest({
            ...selectedRequest,
            customer_name: selectedRequest.customer_name ?? ''
          })}
          onReject={() => handleRejectRequest({
            ...selectedRequest,
            customer_name: selectedRequest.customer_name ?? ''
          })}
        />
      )}
    </div>
  );
};

export default PaymentRequests;
